import { type Href, Link, router } from 'expo-router';
import {
    AlertTriangleIcon,
    BackButton,
    BackButtonIconType,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenContainer,
    H5Regular,
    Icon,
    isWeb,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useViewEvents } from '../../../events/hooks/useViewEvents';

interface SpinwheelGenericErrorProps {
    title?: string;
    description?: string;
    primaryButtonText?: string;
    primaryButtonRoute?: string;
    onPrimaryButtonPress?: () => void;
    secondaryButtonText?: string;
    onSecondaryButtonPress?: () => void;
    showBackButton?: boolean;
    onBackPress?: () => void;
    fallbackRoute?: string;
}

const SpinwheelGenericError = ({
    title = 'Something went wrong',
    description = 'We encountered an unexpected error. Please try again.',
    primaryButtonText = 'Try again',
    primaryButtonRoute = '/onboarding/expenses/disclaimer',
    onPrimaryButtonPress,
    secondaryButtonText,
    onSecondaryButtonPress,
    showBackButton = true,
    onBackPress,
    fallbackRoute = '/',
}: SpinwheelGenericErrorProps) => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('Onboarding/SpinwheelGenericError');

    const handleGoBack = () => {
        if (onBackPress) {
            onBackPress();
        } else {
            router.back();
        }
    };

    const handlePrimaryButton = () => {
        if (onPrimaryButtonPress) {
            onPrimaryButtonPress();
        } else if (primaryButtonRoute) {
            router.navigate(primaryButtonRoute as Href);
        } else {
            router.back();
        }
    };

    const handleSecondaryButton = () => {
        if (onSecondaryButtonPress) {
            onSecondaryButtonPress();
        }
    };

    const Footer = () => {
        return (
            <YStack width="100%" gap={16}>
                {primaryButtonText === 'Contact Fruitful support' ? (
                    <Link href="mailto:support@fruitful.com" asChild>
                        <Button
                            text={primaryButtonText}
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                        />
                    </Link>
                ) : (
                    <Button
                        text={primaryButtonText}
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        onPress={handlePrimaryButton}
                    />
                )}

                {secondaryButtonText != null && secondaryButtonText !== '' && (
                    <Button
                        text={secondaryButtonText}
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.LARGE}
                        onPress={handleSecondaryButton}
                    />
                )}
            </YStack>
        );
    };

    return (
        <FullScreenContainer
            header={
                showBackButton ? (
                    <XStack
                        padding={8}
                        flex={1}
                        justifyContent="flex-start"
                        alignItems="center"
                    >
                        <BackButton
                            fallbackRoute={fallbackRoute as Href}
                            customBackFn={handleGoBack}
                            iconType={BackButtonIconType.ARROW_LEFT}
                        />
                    </XStack>
                ) : undefined
            }
            footer={!isWeb && <Footer />}
        >
            <YStack
                flex={1}
                justifyContent="space-between"
                gap={32}
                marginTop={24}
            >
                <YStack gap={16}>
                    <XStack gap={8} alignItems="center">
                        <H5Regular color="$black">{title}</H5Regular>
                        <View
                            backgroundColor="$orange300"
                            width={28}
                            height={28}
                            borderRadius={8}
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Icon
                                icon={AlertTriangleIcon}
                                color="$orange1100"
                                size={12}
                            />
                        </View>
                    </XStack>
                    <BodyMediumRegular color="$gray1000">
                        {description}
                    </BodyMediumRegular>
                </YStack>
                {isWeb && <Footer />}
            </YStack>
        </FullScreenContainer>
    );
};

export default SpinwheelGenericError;
